import imageCompression from 'browser-image-compression';
import { ALLOWED_PHOTO_TYPES, MAX_PHOTO_BYTES } from '../shared/types';

/**
 * Client-side preparation of a picked photo: HEIC -> JPEG conversion, then
 * shrinking of anything still oversized.
 *
 * Modern phone cameras routinely produce files well past MAX_PHOTO_BYTES, and
 * rejecting them outright loses real submissions. Phone cameras also produce
 * HEIC, which Chrome and Firefox cannot render in an `<img>` at all — a HEIC
 * that uploads perfectly is a broken image on the wall and in the moderation
 * queue for most of the audience, with nothing anywhere flagging it. So HEIC is
 * converted regardless of size, not just when it is too big.
 *
 * This is a CONVENIENCE ONLY: the server still enforces MAX_PHOTO_BYTES and the
 * type allowlist, and nothing here is allowed to relax either.
 *
 * The contract callers depend on: whatever File comes back is the file that
 * must be validated, described to /upload-url, AND sent as the PUT body. The
 * presigned PUT signs `content-length` (X-Amz-SignedHeaders=content-length;host),
 * so pairing the original's size/type with converted OR compressed bytes yields
 * a 403 SignatureDoesNotMatch — and only for photos that actually changed,
 * which is exactly the case a quick manual test misses. Conversion is inside
 * this function for that reason: there is exactly one authoritative File and
 * the caller stores that one.
 */

/** Headroom under the 5MB server limit, in MB. */
const TARGET_MB = 4.5;
/** Far more resolution than the wall thumbnail or the moderation view needs. */
const MAX_DIMENSION = 2400;
const INITIAL_QUALITY = 0.85;

/** Below this, the shrink is not worth mentioning to the user. */
const NOTEWORTHY_RATIO = 0.9;

/**
 * Generous: the JPEG this produces still goes through the compression path
 * above if it lands over the limit, so there is no reason to throw away detail
 * here.
 */
const HEIC_JPEG_QUALITY = 0.9;

/**
 * The decoder never settles its promise if its worker dies — its `onerror`
 * only logs. Everything else in this module fails soft on a throw, but a hang
 * cannot be caught, and a hang leaves the submit form stuck on "processing"
 * with no way out. Long enough for asm.js to grind through a 12MP photo on a
 * mid-range phone; short enough that a wedged worker is not a dead end.
 */
const HEIC_CONVERT_TIMEOUT_MS = 45_000;

/** Some browsers report an empty `file.type` for HEIC, so type alone misses them. */
const HEIC_TYPES = [
  'image/heic',
  'image/heif',
  'image/heic-sequence',
  'image/heif-sequence',
];
const HEIC_EXTENSIONS = ['.heic', '.heif'];

export interface CompressResult {
  /** The file to validate, describe to /upload-url, and PUT. */
  file: File;
  /** True only when compression ran AND meaningfully shrank the file. */
  compressed: boolean;
  /** Size of what the user picked, so the UI can show the before/after. */
  originalSize: number;
}

const isAllowedType = (type: string) =>
  (ALLOWED_PHOTO_TYPES as readonly string[]).includes(type);

/**
 * Detect by type AND by extension. Some browsers hand back a File with an
 * empty `type` for a `.heic` picked from disk, and a type-only check misses
 * exactly those — which then sail through as unconverted HEIC.
 */
export function isHeic(file: File): boolean {
  if (HEIC_TYPES.includes(file.type.toLowerCase())) return true;
  const name = file.name.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => name.endsWith(ext));
}

/** `IMG_0001.HEIC` -> `IMG_0001.jpg`, so the key and Content-Type agree. */
function jpegName(name: string): string {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  return `${stem || 'photo'}.jpg`;
}

/** Reject rather than hang: see HEIC_CONVERT_TIMEOUT_MS. */
function withTimeout<T>(work: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('HEIC conversion timed out')), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/**
 * Decode HEIC and re-encode as JPEG so every browser can display it.
 *
 * Import notes, both load-bearing:
 *
 *  - DYNAMIC import. The decoder is ~3MB; a top-level import would put that in
 *    everyone's bundle to serve the minority who pick a HEIC.
 *  - The `heic-to/csp` entry point specifically, NOT the default `heic-to`.
 *    Our CloudFront CSP is `script-src 'self'` with no 'unsafe-eval', and a
 *    blob: worker inherits the creating document's CSP. The default build (and
 *    the more obvious choice, heic2any) drive libheif through Emscripten embind,
 *    which calls `new Function` ~137 times just to initialise — every one of
 *    them blocked, so conversion would fail on the deployed site while working
 *    fine against a local dev server with no CSP header. The `/csp` build is
 *    published for exactly this constraint and was verified eval-free. If you
 *    switch decoders, re-check that before shipping.
 *
 * Fails soft: on any failure the ORIGINAL file comes back and the pre-existing
 * behaviour takes over. A conversion failure must never block a submission.
 */
async function heicToJpeg(file: File): Promise<File> {
  try {
    const { heicTo } = await import('heic-to/csp');
    const converted = await withTimeout(
      heicTo({ blob: file, type: 'image/jpeg', quality: HEIC_JPEG_QUALITY }),
      HEIC_CONVERT_TIMEOUT_MS,
    );
    // A multi-image HEIC (burst / live photo) can decode to several frames;
    // the first is the one the user thinks they picked.
    const blob = Array.isArray(converted) ? converted[0] : converted;
    if (!blob || blob.size === 0) return file;

    return new File([blob], jpegName(file.name), {
      type: 'image/jpeg',
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}

/**
 * Try the worker first — compressing a 12MB photo on the main thread freezes
 * the page. Worker construction needs CSP `worker-src 'self' blob:` (the
 * library spawns it from a blob URL); if that has not been re-applied to the
 * distribution yet, constructing it throws, so retry inline rather than lose
 * the feature.
 *
 * The library funnels any in-worker failure — construction, decode, encode —
 * through the same rejection, with no way to tell them apart from out here.
 * So this retries on ANY throw, not just a construction failure: a genuine
 * failure partway through a large file does pay for a second full attempt,
 * but the alternative (never retrying) loses the CSP-not-yet-applied case
 * this exists for. `compressPhoto`'s outer try/catch is the backstop if the
 * retry fails too — including the case where a HEIC reached here unconverted
 * because conversion failed soft, which canvas cannot decode outside Safari.
 */
async function runCompression(file: File): Promise<File> {
  const options = {
    maxSizeMB: TARGET_MB,
    maxWidthOrHeight: MAX_DIMENSION,
    initialQuality: INITIAL_QUALITY,
  };
  try {
    return await imageCompression(file, { ...options, useWebWorker: true });
  } catch {
    return await imageCompression(file, { ...options, useWebWorker: false });
  }
}

/**
 * Convert `file` if it is HEIC, then shrink it if it is over the limit. Never
 * throws and never returns something worse than what it was given: on any
 * failure the best file reached so far comes back and the existing size
 * validation decides its fate.
 *
 * Order matters. Conversion runs BEFORE the "already small enough" early
 * return, because a HEIC needs converting whatever its size — an under-5MB
 * HEIC used to sail straight through that early return and land on the wall as
 * a broken image. Conversion also happens to fix oversized HEIC as a side
 * effect: `runCompression` is canvas-based and canvas cannot decode HEIC
 * outside Safari, so it used to throw and the file was rejected as too large.
 * By the time compression runs the file is JPEG, which canvas handles anywhere.
 */
export async function compressPhoto(file: File): Promise<CompressResult> {
  // What the user picked, for the UI's "shrunk from X to Y" — not any
  // intermediate. Read before conversion replaces the reference.
  const originalSize = file.size;

  const working = isHeic(file) ? await heicToJpeg(file) : file;

  const settle = (out: File): CompressResult => ({
    file: out,
    // A HEIC that converts to a much smaller JPEG without ever reaching
    // `imageCompression` is a legitimate "we shrank this", so this compares
    // against what was picked rather than against the conversion output.
    compressed: out.size < originalSize * NOTEWORTHY_RATIO,
    originalSize,
  });

  // Already acceptable: re-encoding buys nothing and can only lose quality.
  if (working.size <= MAX_PHOTO_BYTES) return settle(working);

  let out: File;
  try {
    out = await runCompression(working);
  } catch {
    return settle(working);
  }

  // The library normally emits image/jpeg regardless of input. If it emitted
  // something the server would reject, the pre-compression file is the safer
  // thing to try.
  if (!out || !isAllowedType(out.type)) return settle(working);

  // Never upload something larger than what went in.
  if (out.size >= working.size) return settle(working);

  return settle(out);
}

/** Human-readable byte size for the "we shrank this" note. One decimal, MB. */
export function formatBytes(bytes: number): string {
  const mb = bytes / 1024 / 1024;
  if (mb >= 0.1) return `${mb.toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}
